import { useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { MessageSquare, Search, FolderPlus, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/Mascot";
import { useFolders, newFolder, renameFolder, deleteFolder, foldersChanged } from "@/components/Folders";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const MENU = "bg-[#161B28] border-white/10 min-w-[190px]";
const chip = (on) => `h-9 px-3.5 rounded-full text-sm inline-flex items-center gap-2 whitespace-nowrap border outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 transition-colors ${
  on ? "border-sky-400/60 bg-sky-400/10 text-white" : "border-white/10 text-neutral-400 hover:text-white hover:bg-white/[0.04]"}`;
const dots = "h-7 w-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-white hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70";

function FolderMenu({ f }) {
  const rename = () => { const name = window.prompt("Rename folder", f.name); if (name) renameFolder(f, name); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`Options for folder ${f.name}`} data-testid={`folder-menu-${f.folder_id}`} className={dots}><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={MENU}>
        <DropdownMenuItem onClick={rename} data-testid={`folder-rename-${f.folder_id}`}><Pencil className="h-3.5 w-3.5 mr-2" />Rename folder</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => deleteFolder(f)} className="text-red-300 focus:text-red-200" data-testid={`folder-delete-${f.folder_id}`}><Trash2 className="h-3.5 w-3.5 mr-2" />Delete folder</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FolderBar({ folders, chats, active, onPick }) {
  const count = (id) => chats.filter((c) => c.folder_id === id).length;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="chats-folder-bar">
      <button onClick={() => onPick("all")} className={chip(active === "all")} data-testid="chats-folder-all">All chats</button>
      {folders.map((f) => (
        <span key={f.folder_id} className="inline-flex items-center gap-0.5" data-testid={`chats-folder-${f.folder_id}`}>
          <button onClick={() => onPick(f.folder_id)} className={chip(active === f.folder_id)} data-testid={`chats-folder-pick-${f.folder_id}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: f.color }} />{f.name}
            <span className="text-xs text-neutral-500" data-testid={`folder-count-${f.folder_id}`}>{count(f.folder_id)}</span>
          </button>
          <FolderMenu f={f} />
        </span>
      ))}
      <button onClick={newFolder} className={chip(false)} data-testid="chats-new-folder-btn"><FolderPlus className="h-4 w-4" />New folder</button>
    </div>
  );
}

function MoveMenu({ chat, folders }) {
  const move = async (folderId) => {
    try { await http.put(`/chats/${chat.chat_id}/folder`, { folder_id: folderId }); foldersChanged(); toast.success(folderId ? "Moved to folder" : "Removed from folder"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const createAndMove = async () => { const f = await newFolder(); if (f) move(f.folder_id); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`Move ${chat.title} to a folder`} data-testid={`chats-move-${chat.chat_id}`} className={dots}><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={MENU}>
        <DropdownMenuLabel className="text-xs text-neutral-500 font-normal">Move to folder</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => move(null)} disabled={!chat.folder_id} data-testid={`chats-move-${chat.chat_id}-none`}>No folder</DropdownMenuItem>
        {folders.map((f) => (
          <DropdownMenuItem key={f.folder_id} onClick={() => move(f.folder_id)} disabled={chat.folder_id === f.folder_id} data-testid={`chats-move-${chat.chat_id}-${f.folder_id}`}>
            <span className="h-2 w-2 rounded-full mr-2" style={{ background: f.color }} />{f.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={createAndMove} data-testid={`chats-move-${chat.chat_id}-new`}><FolderPlus className="h-3.5 w-3.5 mr-2" />New folder…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ChatLine({ c, folder, folders }) {
  return (
    <li className="flex items-center gap-1 rounded-xl hover:bg-white/[0.04] transition-colors pr-2">
      <Link to={`/app/c/${c.chat_id}`} data-testid={`chats-item-${c.chat_id}`} className="flex-1 min-w-0 flex items-center gap-3 px-3 py-3 outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 rounded-xl">
        <MessageSquare className="h-4 w-4 text-neutral-500 shrink-0" />
        <span className="flex-1 min-w-0 truncate text-sm text-neutral-100">{c.title}</span>
        {folder && <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-neutral-500 shrink-0" data-testid={`chats-item-folder-${c.chat_id}`}><span className="h-2 w-2 rounded-full" style={{ background: folder.color }} />{folder.name}</span>}
        <span className="text-xs text-neutral-500 shrink-0">{new Date(c.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
      </Link>
      <MoveMenu chat={c} folders={folders} />
    </li>
  );
}

export default function ChatsPage() {
  const { chats } = useOutletContext();
  const folders = useFolders();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("all");
  const byId = useMemo(() => Object.fromEntries(folders.map((f) => [f.folder_id, f])), [folders]);
  const active = picked === "all" || byId[picked] ? picked : "all";
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return chats.filter((c) => (active === "all" || c.folder_id === active) && (!t || c.title.toLowerCase().includes(t)));
  }, [chats, q, active]);
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid="chats-page">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">All chats</h1>
          <p className="text-neutral-400 mt-3">Every conversation, most recent first. Keep subjects together with folders.</p>
        </div>
        {!chats.length && <EmptyState testid="chats-empty" pose="thinking" title="No chats yet" text="Ask Bryn a question to start your first chat." action="New chat" to="/app/new" />}
        {chats.length > 0 && (
          <>
            <FolderBar folders={folders} chats={chats} active={active} onPick={setPicked} />
            <div className="relative">
              <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by title…" aria-label="Filter chats" className="pl-9 h-10 bg-white/5 border-white/10" data-testid="chats-filter" />
            </div>
          </>
        )}
        <ul className="-mx-3" data-testid="chats-list">
          {shown.map((c) => <ChatLine key={c.chat_id} c={c} folder={byId[c.folder_id]} folders={folders} />)}
        </ul>
        {chats.length > 0 && !shown.length && (
          <p className="text-sm text-neutral-500 text-center py-6" data-testid="chats-no-match">{active !== "all" && !q.trim() ? "No chats in this folder yet." : "No chat titles match."}</p>
        )}
      </div>
    </div>
  );
}
